// components/MaintenanceTypeCard.tsx
import React from 'react';

const maintenanceMapping: Record<string, string> = {
    repair: "Repair",
    service: "Service",
    inspection: "Inspection",
    upgrade: "Upgrade",
    other: "Other"
};

const statusMapping: Record<string, string> = {
    scheduled: "Scheduled",
    in_progress: "In Progress",
    completed: "Completed",
    missed: "Missed",
    cancelled: "Cancelled",
    paid: "Paid",
    unpaid: "Unpaid",
};

const statusIcons: Record<string, string> = {
    scheduled: "clock-hour-9 text-info",
    in_progress: "progress text-warning",
    completed: "check text-success",
    cancelled: "x text-danger",
    paid: "currency-dollar text-success",
    unpaid: "currency-dollar-off text-danger",
    missed: "alert-circle text-secondary"
};

const statusBg: Record<string, string> = {
    scheduled: "info",
    in_progress: "warning",
    completed: "success",
    cancelled: "danger",
    paid: "success",
    unpaid: "danger",
    missed: "secondary"
};

// Card for displaying individual maintenance types or status
export const MaintenanceTypeCard = ({ title, value, iconClass, bgClass }: any) => (
    <div className="col-xl-3 col-md-6 d-flex">
        <div className="card flex-fill">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div className="flex-shrink-0 me-2">
                            <span className={`p-2 br-10 ${bgClass} d-flex align-items-center justify-content-center`}>
                                <i className={iconClass} />
                            </span>
                        </div>
                        <div>
                            <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">{title}</p>
                            <h4>{value}</h4>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// Summarize Maintenance Types (Repair, Service, etc.)
export const getMaintenanceTypeSummary = (data: any[]) => {
    const summary: any = { repair: 0, service: 0, inspection: 0, upgrade: 0, other: 0 };
    data.forEach((item: any) => {
        const maintenanceType: any = item.maintenance_type?.toLowerCase();
        if (maintenanceType) {
            summary[maintenanceType]++;
        }
    });
    return summary;
};

// Generate Maintenance Type Cards
export const getMaintenanceTypeCards = (data: any[]) =>
    Object.entries(getMaintenanceTypeSummary(data)).map(([key, value]) => (
        <MaintenanceTypeCard
            key={key}
            title={`${maintenanceMapping[key] || key} Maintenance`}
            value={value}
            iconClass={`ti ti-${key === 'repair' ? 'ti ti-tool text-danger' : key === 'service' ? 'tools text-primary' : key === 'inspection' ? 'search text-warning' : key === 'upgrade' ? 'arrow-up text-success' : 'circle text-info'} fs-18`}
            bgClass={`bg-${key === 'repair' ? 'danger' : key === 'service' ? 'primary' : key === 'inspection' ? 'warning' : key === 'upgrade' ? 'success' : 'info'}-transparent border border-${key}`}
        />
    ));

// Summarize Status (Scheduled, In Progress, etc.)
export const getStatusSummary = (data: any[], totalAssets: number = 0) => {
    const summary: any = {
        scheduled: 0,
        in_progress: 0,
        completed: 0,
        cancelled: 0,
        paid: 0,
        unpaid: 0, 
        missed: 0  
    };

    data.forEach((item: any) => {
        const status = item.status?.toLowerCase();
        if (status && summary.hasOwnProperty(status)) {
            summary[status]++;
            if (status === "paid") {
                summary["completed"]++; // Paid also counts as completed
            }
        }
    });

    const activeCount = summary.scheduled + summary.in_progress + summary.completed;
    summary.missed = Math.max(0, totalAssets - activeCount); // Ensure no negative count
    summary.unpaid = Math.max(0, summary.scheduled + summary.in_progress + summary.completed - summary.paid);

    return summary;
};



// Generate Status Cards
export const getStatusMaintenanceCards = (data: any[]) => {
    const totalAssets = data?.[0]?.total_assets || 0;
    const summary = getStatusSummary(data, totalAssets);

    return Object.entries(summary).map(([key, value]) => (
        <MaintenanceTypeCard
            key={key}
            title={`${statusMapping[key] || key}`}
            value={value}
            iconClass={`ti ti-${statusIcons[key]} fs-18`}
            bgClass={`bg-${statusBg[key]}-transparent border border-${statusBg[key]}`}
        />
    ));
};


// Generate Total Assets Card
export const getTotalAssetsCard = (data: any[]) => {
    const totalAssets = data?.[0]?.total_assets || 0;
    return (
        <MaintenanceTypeCard
            key="total_assets"
            title="Total Assets"
            value={totalAssets}
            iconClass="ti ti-building text-secondary fs-18"
            bgClass="bg-secondary-transparent border border-secondary"
        />
    );
};
